'use client'
import { AccordionTile, LuneTheme, useDevice } from '@lune-fe/lune-ui-lib'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import Image from 'next/image'

import _Link from '@/components/Link'

function Link({ children, href }: { children: React.ReactNode; href: string }) {
    return (
        <_Link sx={{ textDecoration: 'underline' }} href={href} color={'Grey600'} newPage>
            {children}
        </_Link>
    )
}

const FAQs = () => {
    const { palette } = LuneTheme
    const { xsScreen } = useDevice()

    return (
        <Box>
            <Text variant={'h6'} sx={{ mb: 5 }}>
                FAQs
            </Text>
            <AccordionTile
                sx={{
                    background: 'transparent',
                }}
                title={'What is the EU Emissions Trading System (EU ETS)?'}
                icon={<HelpOutlineIcon />}
            >
                <Text variant={'body3'} sx={{ color: palette.Grey600 }}>
                    The{' '}
                    <Link href="https://climate.ec.europa.eu/eu-action/eu-emissions-trading-system-eu-ets_en">
                        EU ETS (EU Emissions Trading System)
                    </Link>{' '}
                    is not a new scheme. The &apos;cap and trade&apos; system was set up in 2005,
                    with the goal to combat climate change by reducing greenhouse gas emissions
                    (GHG). Before, the policy only covered electricity and heat generation, energy
                    intensive sections such as oil refineries, steel works, and the production of
                    iron, aluminum, glass etc. But{' '}
                    <Link href="https://climate.ec.europa.eu/eu-action/transport/reducing-emissions-shipping-sector_en">
                        as of January 2024, the EU ETS now includes maritime transport
                    </Link>
                    .<br />
                    <br />
                    Companies affected by this policy must annually report on their emissions
                    through the EU MRV (Monitoring, Reporting, and Verification) system, and
                    &ldquo;surrender/use&ldquo; purchased ETS allowances (EUAs) to cover these
                    emissions.
                    <br />
                    <br />
                    Effectively, it’s a tax. The aim is to make producing emissions more expensive.
                    Thus, incentivising rapid decarbonisation.
                </Text>
            </AccordionTile>
            <AccordionTile
                sx={{
                    background: 'transparent',
                }}
                title={'What are European Union Allowances (EUAs)?'}
                icon={<HelpOutlineIcon />}
            >
                <Text variant={'body3'} sx={{ color: palette.Grey600 }}>
                    EUAs are a type of carbon allowance: companies affected by the EU ETS will be
                    charged for the amount of CO₂e they emit. EUAs can be bought and sold on the
                    market, and the{' '}
                    <Link href="https://ember-climate.org/data/data-tools/carbon-price-viewer/">
                        variable market price of EUAs
                    </Link>{' '}
                    reflects the cost of reducing emissions.
                </Text>
            </AccordionTile>
            <AccordionTile
                sx={{
                    background: 'transparent',
                }}
                title={`I've already offset my emissions, will they still need to be covered by EUAs?`}
                icon={<HelpOutlineIcon />}
            >
                <Text variant={'body3'} sx={{ color: palette.Grey600 }}>
                    Yes. The EU ETS regulation does not take into account voluntary carbon
                    offsetting. Regardless of whether a company has chosen to voluntarily offset
                    their emissions or not, they must still pay for enough EUAs to cover the cost of
                    the emissions.
                </Text>
            </AccordionTile>
            <AccordionTile
                sx={{
                    background: 'transparent',
                }}
                title={'Does the EU ETS include maritime shipping?'}
                icon={<HelpOutlineIcon />}
            >
                <Text variant={'body3'} sx={{ color: palette.Grey600 }}>
                    Yes, the EU ETS applies to maritime transport. Over time, the scope of shipments
                    covered by the EU ETS will grow.
                    <br />
                    <br />
                    As of 2024, carriers will need to report on and pay the allowances to cover the
                    CO₂ emissions for:
                    <br />
                    <ul>
                        <li>Cargo and passenger ships of or above 5000 gross tonnage (GT)</li>
                        <li>100% of the emissions from intra-EU maritime voyages</li>
                        <li>100% of emissions from ships at berth in EU ports</li>
                        <li>
                            50% of emissions from voyages which start or end at EU ports, where the
                            other destination is outside of the EU
                        </li>
                    </ul>
                    However, there are some exceptions. For example, it does not include port of
                    calls further than 300 nautical miles from EU ports, or emissions resulting from
                    the combustion of biofuels, or stops for obtaining supplies etc. For more
                    information on specific rules and derogations, visit the EU Action hub.
                </Text>
            </AccordionTile>
            <AccordionTile
                sx={{
                    background: 'transparent',
                }}
                title={`Who's responsible for reporting emissions and surrendering allowances?`}
                icon={<HelpOutlineIcon />}
            >
                <Text variant={'body3'} sx={{ color: palette.Grey600 }}>
                    Carriers are responsible for measuring and disclosing their overall emissions.
                    They are also responsible for surrendering EUAs to cover the cost of these
                    emissions.
                    <br />
                    <br />
                    <Link href="https://climate.ec.europa.eu/eu-action/transport/reducing-emissions-shipping-sector/faq-maritime-transport-eu-emissions-trading-system-ets_en#key-players">
                        Learn more
                    </Link>
                    .
                </Text>
            </AccordionTile>
            <AccordionTile
                sx={{
                    background: 'transparent',
                }}
                title={'What are EU ETS surcharges?'}
                icon={<HelpOutlineIcon />}
            >
                <Text variant={'body3'} sx={{ color: palette.Grey600 }}>
                    Carriers have decided to pass on the cost of purchasing EUAs to shippers in the
                    form of a &lsquo;surcharge&lsquo;. These surcharges are not imposed by the EU.
                    <br />
                    <br />
                    It’s important to note that each &lsquo;surcharge&lsquo; is created by
                    individual carriers. Meaning how they calculate the charge, or even the form a
                    surcharge might take can vary from carrier to carrier. For example, some
                    carriers may decide to cover the cost through carbon insetting, bio-fuel
                    shipments, or other alternatives.
                </Text>
            </AccordionTile>
            <AccordionTile
                sx={{
                    background: 'transparent',
                }}
                title={'How do carriers calculate the EU ETS surcharge?'}
                icon={<HelpOutlineIcon />}
            >
                <Text variant={'body3'} sx={{ color: palette.Grey600 }}>
                    The EU ETS asks carriers to surrender one EUA for each tonne of CO₂e they emit,
                    therefore you could calculate the fixed cost per shipment by:
                    <br />
                    <br />
                    <b>CO₂ Emissions per TEU × EUAs price</b>
                    <br />
                    <br />
                    However, the reality is not that simple.
                    <br />
                    <ul>
                        <li>
                            Carriers collect surcharges before the shipment has taken place, when
                            the shipment emissions aren’t known yet
                        </li>
                        <li>EUA price fluctuates over time</li>
                        <li>Carriers bear admin costs for reporting emissions to the EU ETS</li>
                    </ul>
                    Given all these variables, carriers have decided to charge a flat rate per trade
                    lane and container type (i.e. dry vs. reefer). Therefore it’s likely the final
                    surcharge passed on to the shipper could be higher or lower than the actual cost
                    of EUAs needed to cover the emissions produced by that single shipment.
                </Text>
            </AccordionTile>
            <AccordionTile
                sx={{
                    background: 'transparent',
                }}
                title={'How does Lune estimate the ETS cost incurred by carriers?'}
                icon={<HelpOutlineIcon />}
            >
                <Text variant={'body3'} sx={{ color: palette.Grey600 }}>
                    Lune uses its emission calculation technology to provide estimates on the cost
                    the carriers would incur for a given shipment under{' '}
                    <Link href="https://climate.ec.europa.eu/eu-action/transport/reducing-emissions-shipping-sector/faq-maritime-transport-eu-emissions-trading-system-ets_en">
                        the EU ETS rules for 2024
                    </Link>
                    :<br />
                    <ul>
                        <li>
                            We calculate the emissions of each vessel for shipping 1 TEU on each
                            route displayed above
                        </li>
                        <li>We only consider 40% of CO₂ emissions, and ignore other GHG gases</li>
                        <li>
                            We consider whether one or both ports are within the EEA (50% or 100%
                            emissions considered)
                        </li>
                        <li>
                            We multiply the emissions by the 2023 EUA average price of € 80 / tCO₂e
                        </li>
                    </ul>
                </Text>
            </AccordionTile>
            <AccordionTile
                sx={{
                    background: 'transparent',
                }}
                title={'How will the EU ETS scheme change in the future?'}
                icon={<HelpOutlineIcon />}
            >
                <Text variant={'body3'} sx={{ color: palette.Grey600 }}>
                    As the scheme evolves, the total amount of maritime emissions that will need to
                    be covered by EUAs will grow, thus increasing demand for these allowances. For
                    example, as shown in the table below, 70% of emissions will need to be covered
                    by EUAs in 2025, and in 2026 methane and nitrous oxide will also fall under the
                    scope of the EU ETS.
                    <br />
                    <br />
                    The price of EUAs is expected to increase in the long-term, particularly as the
                    EU reduces supply to reflect the greater cost of emitting. In economic terms,
                    greater demand and smaller supply means higher prices. Unless fleets are rapidly
                    decarbonised, shippers could expect surcharge prices to hike over the coming
                    years.
                    <br />
                    <br />
                    <Link href="https://climate.ec.europa.eu/eu-action/eu-emissions-trading-system-eu-ets/emissions-cap-and-allowances_en">
                        Learn more about EUAs
                    </Link>
                    .
                </Text>

                <Box
                    sx={{
                        mt: 3,
                        ...(xsScreen
                            ? {
                                  marginLeft: `-80px !important`,
                                  paddingLeft: `80px`,
                                  overflowX: 'auto',
                                  width: `calc(100% + 152px)`,
                              }
                            : {}),
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            minWidth: xsScreen ? '900px' : 'auto',
                        }}
                    >
                        <Image
                            style={{
                                height: 'auto',
                                objectPosition: 'top',
                                ...(xsScreen
                                    ? {
                                          width: '1144px',
                                          objectFit: 'cover',
                                      }
                                    : {
                                          width: '100%',
                                          objectFit: 'contain',
                                      }),
                            }}
                            src={'/images/eu-ets-table.png'}
                            width={2288}
                            height={1040}
                            alt="EUA Table"
                        />
                    </Box>
                </Box>
                <Text variant={'body3'} sx={{ mt: 3, color: palette.Grey600 }}>
                    <Link href="https://climate.ec.europa.eu/eu-action/transport/reducing-emissions-shipping-sector/faq-maritime-transport-eu-emissions-trading-system-ets_en">
                        FAQ – Maritime transport in EU Emissions Trading System (ETS)
                    </Link>
                </Text>
            </AccordionTile>
        </Box>
    )
}

export default FAQs
